import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { HeaderBlockConfig } from '@front-platform/content-builder';
import { headerBlockResolveConfig } from '@front-platform/content-builder';
import { Component, input } from '@angular/core';

import { MarketplaceKeys } from '@config/keys/shop.keys';
import { LanguageFlagsUrls } from '@config/language-flags.config';
import { ModalsService } from '@core/services/modals/modals.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { SidebarNavigationModalConfig } from '@shared/modals/sidebar-navigation-modal/sidebar-navigation-modal.component';
import { HeaderAtomComponent, LogoOptions } from '@shared/atoms/header-atom/header-atom.component';
export interface HeaderBlockRuntimeConfig {
  'left_blocks'?: unknown[];
  'right_blocks'?: unknown[];
  'navigation_links'?: unknown[];
}

export type HeaderBlockConfigInput = Partial<
  Omit<HeaderBlockConfig, keyof HeaderBlockRuntimeConfig> & HeaderBlockRuntimeConfig
>;
export type HeaderBlockResolvedConfig = Omit<HeaderBlockConfig, keyof HeaderBlockRuntimeConfig> &
  Partial<HeaderBlockRuntimeConfig>;
export interface HeaderBlockContract extends ConfigComponentContract<
  HeaderBlockConfigInput,
  HeaderBlockResolvedConfig
> {}

@Component({
  selector: 'app-header-block',
  templateUrl: './header-block.component.html',
  standalone: true,
  imports: [HeaderAtomComponent],
})
export class HeaderBlockComponent implements HeaderBlockContract {
  readonly config = input<HeaderBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<HeaderBlockConfig, HeaderBlockRuntimeConfig>(
    headerBlockResolveConfig,
    () => ({ config: this.config() }),
  );
  get displayLogo(): boolean {
    return this.resolvedConfig()['display_logo'] as unknown as boolean;
  }
  get backLinkUrl(): string {
    return this.resolvedConfig()['back_link_url'] as unknown as string;
  }
  get logo(): { url: string } | undefined {
    return this.resolvedConfig()['logo'] as unknown as { url: string } | undefined;
  }
  get logoOptions(): LogoOptions | undefined {
    return this.resolvedConfig()['logo_options'] as unknown as LogoOptions | undefined;
  }
  get displayHamburger(): boolean {
    return this.resolvedConfig()['display_hamburger'] as unknown as boolean;
  }
  get displayBackButton(): boolean {
    return this.resolvedConfig()['display_back_button'] as unknown as boolean;
  }
  get leftBlocks(): unknown[] {
    return this.resolvedConfig()['left_blocks'] as unknown as unknown[];
  }
  get rightBlocks(): unknown[] {
    return this.resolvedConfig()['right_blocks'] as unknown as unknown[];
  }
  get navigationLinks(): unknown[] {
    return this.resolvedConfig()['navigation_links'] as unknown as unknown[];
  }
  get positionAbsoluteOnMobile(): boolean {
    return this.resolvedConfig()['position_absolute_on_mobile'] as unknown as boolean;
  }
  get positionAbsoluteOnDesktop(): boolean {
    return this.resolvedConfig()['position_absolute_on_desktop'] as unknown as boolean;
  }
  get hamburgerColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['hamburger_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }
  get labelColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['label_color'] as unknown as IContentBuilderFieldColor;
  }
  get iconColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['icon_color'] as unknown as IContentBuilderFieldColor;
  }
  get iconBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['icon_background_color'] as unknown as IContentBuilderFieldColor;
  }
  get buttonTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor;
  }
  get buttonBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor;
  }
  get shopSearchLocationFieldBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_search_location_field_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopSearchLocationFieldTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_search_location_field_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopSearchLocationFieldPlaceholderColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_search_location_field_placeholder_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopSearchLocationFieldIconColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_search_location_field_icon_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }

  languageFlagUrl?: string;

  constructor(
    private readonly modalsService: ModalsService<SidebarNavigationModalConfig>,
    private settingsSelector: SettingsStoreSelector,
    private marketplaceSelector: MarketplaceStoreSelector,
  ) {
    if (this.marketplaceSelector.marketplace[MarketplaceKeys.Locales]?.length > 1) {
      this.languageFlagUrl = LanguageFlagsUrls.get(this.settingsSelector.app.locale);
    }
  }

  openSidebarNavigation(): void {
    this.modalsService.open('sidebar-navigation-modal');
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(HeaderBlockComponent, { 'keyAliases': {} });
